import { BatmanService, Loader, LogService, ValidatorHelper } from '@hub/shared'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './input-document-cnpj.component.scoped.scss'
import './input-document-cnpj.component.scss'

export interface Props extends React.HTMLAttributes<HTMLDivElement> {
  label?: any
  disabled?: boolean
  readOnly?: boolean
  errorSpan?: boolean
  loading?: boolean
  required?: boolean
  externalError?: any
  id?: string
  name?: string
  externalIcon?: any
  errorIcon?: any
  value?: string
  backgroundColor?: string
  requiredOnLoad?: boolean
  type?: 'text' | 'password'
  onChange?: (value: any) => any
  onValidate?: (event: any) => any
}

export interface State {
  error: string
  value: string
  cnpjData: Object
  validatedDocument: boolean
  loading?: boolean
}

enum Situation {
  baixado = 'cnpjPullDown',
  baixada = 'cnpjPullDown',
  suspenso = 'cnpjSuspended',
  suspensa = 'cnpjSuspended',
  inapto = 'cnpjInapt',
  inapta = 'cnpjInapt',
}

export default ({
  label,
  disabled = false,
  readOnly = false,
  errorSpan = true,
  loading,
  requiredOnLoad = false,
  required,
  externalError,
  id,
  name,
  errorIcon = <i className="fa-regular fa-triangle-exclamation"></i>,
  externalIcon,
  value = '',
  backgroundColor = '',
  className = '',
  type = 'text',
  onChange,
  onValidate,
}: Props) => {
  const timestamp = `random-id-${(1 + Date.now() + Math.random()).toString().replace('.', '')}`
  const validatorHelper = useMemo(() => new ValidatorHelper(), [])
  const logService = useMemo(() => new LogService(), [])
  const batmanService = useMemo(() => new BatmanService(), [])

  const { t } = useTranslation('inputDocumentCnpj')

  const [state, setState] = useState<State>({
    value: '',
    error: '',
    cnpjData: {},
    validatedDocument: false,
    loading: false,
  })

  logService.init('Input Document Cnpj')

  const setMask = useCallback((cnpj: string) => {
    const num = cnpj.replace(/[^\d]/g, '')
    const len = num.length

    if (len <= 5) {
      cnpj = num.replace(/(\d{2})(\d{1,3})/g, '$1.$2')
    } else if (len <= 8) {
      cnpj = num.replace(/(\d{2})(\d{3})(\d{1,3})/g, '$1.$2.$3')
    } else if (len <= 12) {
      cnpj = num.replace(/(\d{2})(\d{3})(\d{3})(\d{1,4})/g, '$1.$2.$3/$4')
    } else {
      cnpj = num.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{1,2})/g, '$1.$2.$3/$4-$5')
    }
    return cnpj
  }, [])

  useEffect(() => {
    if (state.value !== value) {
      setState((prevState) => ({
        ...prevState,
        value: setMask(value),
      }))
    }
  }, [state.value, value, setMask])

  const checkCnpjData = useCallback(
    async (value: string) => {
      try {
        setState((prevState) => ({ ...prevState, loading: true }))
        return await batmanService.getCnpjInfos(value.replace(/\D/g, ''))
      } catch (error) {
        logService.error({ message: `Error on get CNPJ infos from ${value}`, error })
        return {}
      } finally {
        setState((prevState) => ({ ...prevState, loading: false }))
      }
    },
    [batmanService, logService]
  )

  const handleChange = useCallback(
    async (event: any) => {
      const value = event?.target?.value
      const newState: State = { value: '', cnpjData: {}, validatedDocument: false, error: '' }

      newState.value = setMask(value)

      setState((prevState) => ({
        ...prevState,
        value: newState.value,
      }))

      if (externalError) {
        newState.error = ''
        newState.validatedDocument = true
      } else if (!!required && (!newState.value || newState.value.length < 1)) {
        newState.error = t('required')
        newState.validatedDocument = false
      } else if (!validatorHelper.cnpj(newState.value.replace(/\D/g, ''))) {
        newState.error = t('invalidCnpj')
        newState.validatedDocument = false
      } else if (newState.value.length === 18) {
        const data = await checkCnpjData(newState.value)
        if (data?.situacao === 'ATIVA') {
          newState.cnpjData = data
          newState.validatedDocument = true
        }
        // Se necessário voltar os erros, remover esse else e colocar o código comentado
        else {
          newState.error = ''
          newState.validatedDocument = true
        }
      }

      setState((prevState) => ({
        ...prevState,
        cnpjData: newState.cnpjData,
        validatedDocument: newState.validatedDocument,
        error: newState.error,
      }))

      if (onValidate && typeof onValidate === 'function') {
        onValidate(!newState.error && !externalError && newState.validatedDocument)
      }

      if (onChange && typeof onChange === 'function') {
        const newData = {
          cnpj: newState.value.replace(/[^\d]/g, ''),
          cnpjData: newState.cnpjData,
        }
        onChange(newData)
      }
    },
    [checkCnpjData, externalError, onChange, onValidate, required, t, validatorHelper, setMask]
  )

  return (
    <div className={`wrapper-all-inside-input ${className}`}>
      {label ? (
        <label className={'label ' + `${externalError || state.error ? 'error ' : ''}`} htmlFor={id || timestamp}>
          {label}
        </label>
      ) : null}
      <div className={'no-padding wrapper-input wrapper-input-squid ' + `${externalError || state.error ? 'error ' : ''}`}>
        <input
          className={
            'col input ' +
            `${readOnly ? 'readonly ' : ''}` +
            `${disabled ? 'disabled ' : ''}` +
            `${loading || state.loading ? 'loading ' : ''}` +
            `${externalError || state.error ? 'error ' : ''}` +
            `${externalIcon ? 'has-icon-external ' : ''}`
          }
          style={{
            backgroundColor: backgroundColor,
          }}
          id={id || timestamp}
          type={type}
          inputMode="numeric"
          maxLength={18}
          name={name || timestamp}
          placeholder={'##.###.###/####-##'}
          value={state.value}
          required={required}
          disabled={disabled}
          readOnly={readOnly || loading || state.loading}
          onChange={handleChange}
        />
      </div>
      {loading || state.loading ? <Loader size="small" className={`display-block loader-input ${!label ? 'no-label' : ''}`} /> : null}
      {(externalIcon && !loading) || state.loading ? (
        <span className={'icon icon-external ' + `${!label ? 'no-label ' : ''}`}>{externalIcon}</span>
      ) : null}
      {errorSpan ? (
        <div className={'box-validation box-invalid show '}>
          {requiredOnLoad && !state.value && !state.error && !externalError ? (
            <div>
              <i className="fa-solid fa-diamond-exclamation" style={{ color: 'var(--gold)' }}></i>
              {t('globalPayments:emptyField')}
            </div>
          ) : null}
          {state.error || externalError ? errorIcon || <i className="fa-regular fa-triangle-exclamation"></i> : null}
          {externalError ? externalError : ''}
          {state.error && !externalError ? <span className={'message-error'} dangerouslySetInnerHTML={{ __html: state.error }} /> : ''}
        </div>
      ) : null}
    </div>
  )
}
